import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { EstimateDto, SALE_TYPE } from "./types.ts";

/**
 * Renders the byte-identical "content header" block shared by all three
 * estimate forms (jinyoung / mun / comparison):
 *
 *   - `content.contentType` RadioGroup (SALE_TYPE)
 *   - `content.deliveryDate` TextField
 *   - `content.contentTitle` TextField
 *   - `content.deliveryLocation` TextField
 *   - `content.personInCharge` TextField
 *
 * Also wires the `saleType` watch + the `useEffect` that flips the
 * default `deliveryDate` when the SALE_TYPE radio toggles between
 * "판매" (10) and "임대" (3).
 *
 * Must be rendered inside an existing `FormProvider<EstimateDto>`.
 */
export default function ContentHeaderFields() {
  const { control, setValue } = useFormContext<EstimateDto>();

  const saleType = useWatch({
    control,
    name: "content.contentType",
  });

  React.useEffect(() => {
    if (saleType === "판매") {
      setValue("content.deliveryDate", 10, { shouldValidate: true });
    } else if (saleType === "임대") {
      setValue("content.deliveryDate", 3, { shouldValidate: true });
    }
  }, [saleType, setValue]);

  return (
    <>
      <Controller
        name={`content.contentType`}
        control={control}
        render={({ field }) => (
          <RadioGroup
            row
            {...field}
            onChange={(e) =>
              field.onChange(e.target.value)
              // handler
            }
            defaultValue={SALE_TYPE[0]}
          >
            {SALE_TYPE.map((value) =>
              <FormControlLabel
                value={value}
                control={<Radio />}
                label={value}
              />
            )}
          </RadioGroup>
        )}
      />
      <Controller
        name={`content.deliveryDate`}
        control={control}
        defaultValue={3}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            type="number"
            label="납기일"
            error={!!fieldState.error}
            sx={{ width: 100 }}
            onChange={(e) => field.onChange(Number(e.target.value))}
          />
        )}
      />
      <Controller
        name={`content.contentTitle`}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="제목"
            error={!!fieldState.error}
            sx={{ width: 150 }}
          />
        )}
      />
      <Controller
        name={`content.deliveryLocation`}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="납품 장소"
            error={!!fieldState.error}
            sx={{ width: 150 }}
          />
        )}
      />
      <Controller
        name={`content.personInCharge`}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="담당자"
            error={!!fieldState.error}
            sx={{ width: 150 }}
          />
        )}
      />
    </>
  );
}
